/**
 * QueueBoardBuilder — full-screen Display Board editor in the Queue-journey-
 * builder pattern: palette of content blocks on the LEFT (click or drag to
 * add), live TV preview in the CENTRE (fed by the selected queues' real
 * config + sample customers), and ALL board options + the selected block's
 * inspector on the RIGHT. Save AWAITS saveMessageBoard (no fake
 * interactivity); Cancel discards.
 */
import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { alpha, Btn, CopyableUrl, Icon, IconBtn, T } from "@v0y4ge/jrni-ui";
import { toast } from "../../shell/toast";
import { NumberField } from "../../lib/NumberField";
import { useTenantPublicConfig } from "../../lib/public-url";
import { saveMessageBoard, type MessageBoard, type QueueSummary, type PublicDisplayQueue } from "./queues-api";
import { qHoursLabel } from "./queue-hours";
import { useT } from "@v0y4ge/i18n-runtime";
import {
  BOARD_BLOCKS, BoardQueueCard, boardColors, boardPublicUrl, boardBlockUid, defaultBoardBlocks,
  type BoardBlockRec, type BoardNameFormat,
} from "./board-blocks";

// ui-config — sample names for the board PREVIEW render only (never data)
const SAMPLE = ["Maria Gonzales", "Marcus Lee", "Priya Shah", "Jordan Ellis", "Sofia Marino", "Devon Clark", "Amara Diallo", "Theo Brandt"];
/** Preview data cooked from the queue's REAL config (services, hours, prefix) + sample customers. */
function sampleDisplayQueue(q: QueueSummary, i: number): PublicDisplayQueue {
  const pfx = q.ticketPrefix || q.name.charAt(0).toUpperCase();
  const person = (n: number, position: number) => ({ name: SAMPLE[(i * 3 + n) % SAMPLE.length], ticket: `${pfx}-${104 + i * 20 + n}`, position });
  return {
    id: q.id, name: q.name, hoursLabel: qHoursLabel(q.hours),
    nowServing: person(0, 0),
    called: [person(1, 1), person(2, 2)],
    upNext: [person(3, 1), person(4, 2), person(5, 3)],
    services: (q.services.length ? q.services : ["Walk-in"]).slice(0, 4).map((s, si) => ({ name: s, waiting: 1 + ((i + si) % 3) })),
    waiting: 4 + i * 2, estWaitMins: 12 + i * 4,
  };
}

const field: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 5 };
const input: CSSProperties = { width: "100%", minHeight: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" };
const secLabel: CSSProperties = { fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: ".06em" };

export function QueueBoardBuilder({ board, queues, onClose, onSaved }:
  { board: MessageBoard | null; queues: QueueSummary[]; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  useTenantPublicConfig();
  const [name, setName] = useState(board?.name ?? "");
  const [queueIds, setQueueIds] = useState<string[]>(board?.queueIds ?? queues.slice(0, 1).map((q) => q.id));
  const [theme, setTheme] = useState<MessageBoard["theme"]>(board?.theme ?? "dark");
  const [showWait, setShowWait] = useState(board?.showWaitEstimates ?? true);
  const [nameFormat, setNameFormat] = useState<BoardNameFormat>(board?.nameFormat ?? "first-initial");
  const [message, setMessage] = useState(board?.message ?? "Welcome — take a seat, we'll text you when it's your turn.");
  const [blocks, setBlocks] = useState<BoardBlockRec[]>(() => board?.blocks?.length ? JSON.parse(JSON.stringify(board.blocks)) : defaultBoardBlocks(board?.layout ?? "now-serving"));
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const dragRef = useRef<{ type: string | null; uid: string | null }>({ type: null, uid: null });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dark = theme === "dark";
  const C = boardColors(dark);
  const previewQs = useMemo(() =>
    queueIds.map((id) => queues.find((q) => q.id === id)).filter(Boolean).map((q, i) => sampleDisplayQueue(q as QueueSummary, i)),
    [queueIds, queues]);
  const usedTypes = useMemo(() => new Set(blocks.map((b) => b.type)), [blocks]);
  const selected = blocks.find((b) => b.uid === selectedUid) ?? null;
  const selectedDef = selected ? BOARD_BLOCKS.find((d) => d.id === selected.type) : null;

  const spawn = (type: string): BoardBlockRec => ({ uid: boardBlockUid(), type, cfg: { ...(BOARD_BLOCKS.find((d) => d.id === type)?.defaults ?? {}) } });
  const addBlock = (type: string, beforeUid?: string | null) => {
    const def = BOARD_BLOCKS.find((d) => d.id === type);
    if (!def || (!def.multi && usedTypes.has(type))) return;
    const nb = spawn(type);
    setBlocks((cur) => {
      const i = beforeUid ? cur.findIndex((b) => b.uid === beforeUid) : -1;
      return i < 0 ? [...cur, nb] : [...cur.slice(0, i), nb, ...cur.slice(i)];
    });
    setSelectedUid(nb.uid);
  };
  const removeBlock = (uid: string) => { setBlocks((cur) => cur.filter((b) => b.uid !== uid)); if (uid === selectedUid) setSelectedUid(null); };
  const moveBlock = (uid: string, dir: number) => setBlocks((cur) => {
    const i = cur.findIndex((b) => b.uid === uid); const j = i + dir;
    if (i < 0 || j < 0 || j >= cur.length) return cur;
    const next = cur.slice(); const [it] = next.splice(i, 1); next.splice(j, 0, it); return next;
  });
  const patchCfg = (uid: string, patch: Record<string, unknown>) => setBlocks((cur) => cur.map((b) => b.uid === uid ? { ...b, cfg: { ...b.cfg, ...patch } } : b));
  const onDrop = (beforeUid: string | null) => {
    const src = dragRef.current;
    dragRef.current = { type: null, uid: null };
    setDragOver(null);
    if (src.type) { addBlock(src.type, beforeUid); return; }
    if (src.uid && src.uid !== beforeUid) setBlocks((cur) => {
      const found = cur.find((b) => b.uid === src.uid);
      if (!found) return cur;
      const without = cur.filter((b) => b.uid !== src.uid);
      const i = beforeUid ? without.findIndex((b) => b.uid === beforeUid) : -1;
      return i < 0 ? [...without, found] : [...without.slice(0, i), found, ...without.slice(i)];
    });
  };

  const save = async () => {
    if (!name.trim()) { setErr("Give the board a name."); return; }
    if (queueIds.length === 0) { setErr("Pick at least one queue to show."); return; }
    if (blocks.length === 0) { setErr("Add at least one content block from the palette."); return; }
    setSaving(true); setErr(null);
    try {
      // layout stays derived for back-compat consumers (list column, legacy renderers).
      const layout: MessageBoard["layout"] = usedTypes.has("up-next") ? "full-list" : "now-serving";
      await saveMessageBoard({ id: board?.id ?? `mb_${Date.now().toString(36)}`, name: name.trim(), queueIds, locationId: board?.locationId ?? null, layout, theme, showWaitEstimates: showWait, nameFormat, message, blocks });
      toast({ tone: "success", title: board ? "Board updated" : "Board created — public link ready", subtitle: board ? name.trim() : "Copy the display link from the board's ⋯ menu and open it on the lobby screen." });
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };

  /** Builder wrapper for each preview block: click-select, drag-reorder, drop-before. */
  const wrap = (b: BoardBlockRec, node: ReactNode) => (
    <div draggable
      onClick={(e) => { e.stopPropagation(); setSelectedUid((c) => c === b.uid ? null : b.uid); }}
      onDragStart={(e) => { e.stopPropagation(); dragRef.current = { type: null, uid: b.uid }; }}
      onDragEnd={() => { dragRef.current = { type: null, uid: null }; setDragOver(null); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(b.uid); }}
      onDragLeave={() => setDragOver((c) => c === b.uid ? null : c)}
      onDrop={(e) => { e.stopPropagation(); onDrop(b.uid); }}
      data-testid="qboard-builder-block" data-id={b.uid}
      style={{ position: "relative", borderRadius: 8, cursor: "pointer", outline: selectedUid === b.uid ? `2px solid ${C.accent}` : dragOver === b.uid ? `2px dashed ${C.accent}` : "2px solid transparent", outlineOffset: 4 }}>
      {node ?? <div style={{ fontSize: 12, color: C.soft, fontStyle: "italic", padding: "4px 0" }}>{BOARD_BLOCKS.find((d) => d.id === b.type)?.l} {t('queues.boardBuilder.text9')}</div>}
    </div>
  );

  const radio = (on: boolean): CSSProperties => ({ flex: 1, minHeight: 36, borderRadius: 8, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n1, fontSize: 12.5, fontWeight: 600, fontFamily: T.font, cursor: "pointer" });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: T.canvas, display: "flex", flexDirection: "column" }} role="dialog" aria-label={board ? `Edit ${board.name}` : "New display board"}>
      {/* Header */}
      <div style={{ padding: "11px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 11, flexShrink: 0, background: T.white }}>
        <span style={{ width: 32, height: 32, borderRadius: 8, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.browser size={15}/></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{board ? /* TODO(i18n): replace template-literal with t('queues.boardBuilder.text10', { ... }) */`Edit display board — ${board.name}` : t('queues.boardBuilder.text11')}</div>
          <div style={{ fontSize: 11.5, color: T.soft }}>{t('queues.boardBuilder.text12')}</div>
        </div>
        {err ? <span style={{ fontSize: 12, color: T.red1, fontWeight: 600, padding: "4px 10px", background: alpha(T.red1, 8), border: `1px solid ${alpha(T.red1, 30)}`, borderRadius: 999 }}>{err}</span> : null}
        <Btn variant="ghost" onClick={onClose}>{t('queues.boardBuilder.text13')}</Btn>
        <Btn onClick={() => { void save(); }} disabled={saving} testid="qboard-builder-save-btn" style={{ opacity: saving ? .6 : 1 }}>{saving ? t('queues.boardBuilder.text14') : board ? t('queues.boardBuilder.text15') : t('queues.boardBuilder.text16')}</Btn>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Left — content palette */}
        <div style={{ width: 240, background: T.white, borderRight: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 8, overflow: "auto", flexShrink: 0 }}>
          <div style={secLabel}>{t('queues.boardBuilder.text17')}</div>
          {BOARD_BLOCKS.map((b) => {
            const used = !b.multi && usedTypes.has(b.id);
            return (
              <div key={b.id} draggable={!used} onClick={() => addBlock(b.id)}
                onDragStart={() => { dragRef.current = { type: b.id, uid: null }; }}
                onDragEnd={() => { dragRef.current = { type: null, uid: null }; setDragOver(null); }}
                data-testid="qboard-builder-palette-item" data-id={b.id}
                style={{ padding: "10px 12px", border: `1px solid ${used ? T.p1 : T.border}`, borderRadius: 8, background: used ? T.p4 : T.white, cursor: used ? "default" : "pointer", position: "relative" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: used ? T.p1 : T.n1 }}>{b.l}</div>
                <div style={{ fontSize: 11, color: T.soft, marginTop: 2, lineHeight: 1.4 }}>{b.blurb}</div>
                {used ? <span style={{ position: "absolute", top: 8, right: 8, width: 15, height: 15, borderRadius: 999, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.check size={9}/></span> : null}
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.5, marginTop: 4 }}>{t('queues.boardBuilder.text18')}</div>
        </div>

        {/* Centre — live TV preview */}
        <div style={{ flex: 1, overflow: "auto", minWidth: 0 }} onClick={() => setSelectedUid(null)}>
          <div style={{ minHeight: "100%", background: C.bg, color: C.fg, fontFamily: C.font, display: "flex", flexDirection: "column", padding: "36px 44px 28px", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 28 }}>
              <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.3 }}>{t('queues.boardBuilder.text19')}</span>
              <span style={{ fontSize: 15, color: C.soft }}>{name || "Untitled board"}</span>
              <span style={{ marginLeft: "auto", fontSize: 14, color: C.soft }}>{new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
            </div>
            {previewQs.length ? (
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${Math.max(1, Math.min(3, previewQs.length))}, minmax(0, 1fr))`, gap: 24, alignItems: "stretch" }}>
                {previewQs.map((q) => (
                  <BoardQueueCard key={q.id} q={q} blocks={blocks} C={C} dark={dark} nameFormat={nameFormat} showWait={showWait} wrap={wrap} />
                ))}
              </div>
            ) : (
              <div style={{ margin: "auto", fontSize: 18, color: C.soft }}>{t('queues.boardBuilder.text20')}</div>
            )}
            <div onDragOver={(e) => { e.preventDefault(); setDragOver("end"); }} onDragLeave={() => setDragOver((c) => c === "end" ? null : c)} onDrop={() => onDrop(null)}
              style={{ marginTop: 20, padding: "14px 16px", borderRadius: 10, border: `2px dashed ${dragOver === "end" ? C.accent : C.border}`, textAlign: "center", color: C.soft, fontSize: 13 }}>
              {t('queues.boardBuilder.text21')}</div>
            <div style={{ marginTop: 20, textAlign: "center", fontSize: 17, color: C.soft }}>{message}</div>
          </div>
        </div>

        {/* Right — board options + selected-block inspector */}
        <div style={{ width: 300, background: T.white, borderLeft: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14, overflow: "auto", flexShrink: 0 }}>
          {selected && selectedDef ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={secLabel}>{t('queues.boardBuilder.text22')}{selectedDef.l}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <IconBtn size={28} label={t('queues.boardBuilder.label')} onClick={() => moveBlock(selected.uid, -1)}><Icon.chev dir="up" size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.boardBuilder.label2')} onClick={() => moveBlock(selected.uid, +1)}><Icon.chev size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.boardBuilder.label3')} onClick={() => removeBlock(selected.uid)}><Icon.trash size={12}/></IconBtn>
                </div>
              </div>
              {"label" in selected.cfg || selected.type === "now-serving" || selected.type === "called" || selected.type === "up-next" ? (
                <div><div style={field}>{t('queues.boardBuilder.text23')}</div><input value={String(selected.cfg.label ?? "")} onChange={(e) => patchCfg(selected.uid, { label: e.target.value })} style={input} data-testid="qboard-builder-label-input" /></div>
              ) : null}
              {selected.type === "called" || selected.type === "up-next" ? (
                <div style={{ maxWidth: 140 }}><div style={field}>{t('queues.boardBuilder.text24')}</div><NumberField value={Number(selected.cfg.max ?? (selected.type === "called" ? 3 : 6))} min={1} onChange={(n) => patchCfg(selected.uid, { max: n })} ariaLabel={t('queues.boardBuilder.ariaLabel')} style={input} /></div>
              ) : null}
              {selected.type === "hours" ? (
                <div><div style={field}>{t('queues.boardBuilder.text25')}</div><input value={String(selected.cfg.prefix ?? "Open")} onChange={(e) => patchCfg(selected.uid, { prefix: e.target.value })} style={input} /></div>
              ) : null}
              {selected.type === "text" ? (
                <div><div style={field}>{t('queues.boardBuilder.text26')}</div><textarea value={String(selected.cfg.text ?? "")} onChange={(e) => patchCfg(selected.uid, { text: e.target.value })} rows={3} style={{ ...input, padding: 10, resize: "vertical", minHeight: 64 }} /></div>
              ) : null}
              {selected.type === "stats" ? <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{t('queues.boardBuilder.text27')}{" "}{showWait ? t('queues.boardBuilder.text28') : ""}{t('queues.boardBuilder.text29')}</div> : null}
              {selected.type === "services" ? <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{t('queues.boardBuilder.text30')}</div> : null}
              <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "2px 0" }} />
            </>
          ) : null}
          <div style={secLabel}>{t('queues.boardBuilder.text31')}</div>
          <div><div style={field}>{t('queues.boardBuilder.text32')}</div><input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('queues.boardBuilder.placeholder')} style={input} data-testid="qboard-builder-name-input" /></div>
          <div>
            <div style={field}>{t('queues.boardBuilder.text33')}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {queues.map((q) => {
                const on = queueIds.includes(q.id);
                return (
                  <button key={q.id} onClick={() => setQueueIds((cur) => on ? cur.filter((x) => x !== q.id) : [...cur, q.id])} aria-pressed={on} data-testid="qboard-builder-queue-chip" data-id={q.id}
                    style={{ minHeight: 32, padding: "0 11px", borderRadius: 999, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n1, fontSize: 12, fontWeight: 600, fontFamily: T.font, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {on ? <Icon.check size={10}/> : <Icon.plus size={10}/>}{q.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div style={field}>{t('queues.boardBuilder.text34')}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setTheme("dark")} aria-pressed={theme === "dark"} style={radio(theme === "dark")} data-testid="qboard-builder-theme-dark">{t('queues.boardBuilder.text35')}</button>
              <button onClick={() => setTheme("light")} aria-pressed={theme === "light"} style={radio(theme === "light")} data-testid="qboard-builder-theme-light">{t('queues.boardBuilder.text36')}</button>
            </div>
          </div>
          <div>
            <div style={field}>{t('queues.boardBuilder.text37')}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {([["full", "Full name"], ["first-initial", "First + initial"], ["first", "First only"], ["ticket", "Ticket only"]] as [BoardNameFormat, string][]).map(([id, lbl]) => (
                <button key={id} onClick={() => setNameFormat(id)} aria-pressed={nameFormat === id} style={{ ...radio(nameFormat === id), minHeight: 34 }} data-testid={`qboard-builder-nameformat-${id}`}>{lbl}</button>
              ))}
            </div>
            {nameFormat === "ticket" ? <div style={{ fontSize: 11, color: T.soft, marginTop: 5, lineHeight: 1.5 }}>{t('queues.boardBuilder.text38')}</div> : null}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.n1, cursor: "pointer" }}>
            <input type="checkbox" checked={showWait} onChange={(e) => setShowWait(e.target.checked)} data-testid="qboard-builder-showwait-checkbox" style={{ width: 15, height: 15, accentColor: T.p1 }} />
            {t('queues.boardBuilder.text39')}</label>
          <div><div style={field}>{t('queues.boardBuilder.text40')}</div><textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} data-testid="qboard-builder-message-input" style={{ ...input, padding: 10, resize: "vertical", minHeight: 52 }} /></div>
          {board ? (
            <div>
              <div style={field}>{t('queues.boardBuilder.text41')}</div>
              <CopyableUrl url={boardPublicUrl(board.id)} copyLabel="Copy display link" copiedMessage="Display link copied" data-testid="qboard-builder-public-link" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
